import { MessageSquare, X } from "lucide-react";
import { useMemo, useState } from "react";

import { DialogoCambiosSinGuardar } from "@/components/DialogoCambiosSinGuardar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/ui/spinner";
import { urlDeLaFotoDeLaFila } from "@/lib/informes";
import { cn } from "@/lib/utils";

import {
  GrupoDeIdentificacion,
  GrupoDeValorizacion,
  RotuloDeGrupo,
  ValorEstimado,
} from "./CamposDeConstruccion";
import { FotosDeLaFila } from "./FotosDeLaFila";
import { RegistroDeTerrenoDeLaConstruccion } from "./RegistroDeTerrenoDeLaConstruccion";
import { InsigniaTerreno } from "./TablaDeConstrucciones";
import {
  formularioDesdeConstruccion,
  formularioDesdeElItemDeTerreno,
  type FormularioDeConstruccion,
} from "./construcciones";
import { CLAVE_DEL_FORMULARIO, type FilaDelCuadro, type ModoDelTerreno } from "./cuadroDeConstrucciones";
import { mismosCampos } from "./DialogoDeEdicionDeFila";
import { horaDeCaptura } from "./registroDeTerreno";

/** Lo que el modal entrega al guardar. */
export interface GuardadoDeLaConstruccion {
  formulario: FormularioDeConstruccion;
  validada: boolean;
  /** Las fotos elegidas antes de que la fila exista (solo en una construcción de terreno sin guardar). */
  fotos: File[];
}

interface DialogoDeConstruccionProps {
  /** La fila que se abre, o `null` con el modal cerrado. */
  fila: FilaDelCuadro | null;
  informeId: number;
  modo: ModoDelTerreno;
  encargado: string | null;
  ocupada: boolean;
  onGuardar: (fila: FilaDelCuadro, guardado: GuardadoDeLaConstruccion) => void;
  onCerrar: () => void;
  onSubirFoto: (filaId: number, archivo: File) => void;
  onQuitarFoto: (filaId: number, fotoId: number) => void;
  onLeyendaDeFoto?: (filaId: number, fotoId: number, leyenda: string | null) => void;
  onAbrirFoto: (uuid: string) => void;
}

/**
 * El modal "Editar construcción": a la izquierda los campos, las notas que el
 * encargado dejó sobre cada dato y las fotos; a la derecha, el registro de
 * terreno de ESTA construcción.
 *
 * SE REMONTA POR FILA (`key`): el estado de lo que se está escribiendo nace de
 * la fila que se abre y muere con el modal, así que abrir otra nunca hereda un
 * borrador ajeno.
 */
export function DialogoDeConstruccion(props: DialogoDeConstruccionProps) {
  return props.fila === null ? null : <Contenido key={props.fila.clave} {...props} fila={props.fila} />;
}

function Contenido({
  fila,
  informeId,
  modo,
  encargado,
  ocupada,
  onGuardar,
  onCerrar,
  onSubirFoto,
  onQuitarFoto,
  onLeyendaDeFoto,
  onAbrirFoto,
}: DialogoDeConstruccionProps & { fila: FilaDelCuadro }) {
  const guardada = fila.fila;

  const inicial = useMemo(
    () => (guardada ? formularioDesdeConstruccion(guardada) : formularioDesdeElItemDeTerreno(fila.item!)),
    // La fila guardada llega de la caché y cambia de identidad con cada
    // refetch (por ejemplo, al subir una foto): lo que se escribe no se pisa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fila.clave],
  );

  const [formulario, setFormulario] = useState<FormularioDeConstruccion>(inicial);
  const [casilla, setCasilla] = useState(fila.validada);
  const [casillaTocada, setCasillaTocada] = useState(false);
  const [fotosPendientes, setFotosPendientes] = useState<File[]>([]);
  const [rechazoDeFoto, setRechazoDeFoto] = useState<string | null>(null);
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);

  const cambio = !mismosCampos(formulario, inicial);
  const hayCambios = cambio || casillaTocada || fotosPendientes.length > 0;

  /* LO VALIDADO NO SOBREVIVE A UN CAMBIO: si la fila estaba validada y se
     tocó un dato, vuelve a quedar sin validar — salvo que la persona haya
     vuelto a marcar la casilla en el modal, que es decir "ya lo revisé". */
  const vuelveASinValidar = fila.validada && cambio && !casillaTocada;
  const validadaAlGuardar = vuelveASinValidar ? false : casilla;

  const conNota = useMemo(
    () => new Set(fila.notas.map((n) => CLAVE_DEL_FORMULARIO[n.campo])),
    [fila.notas],
  );

  function escribir(cambios: Partial<FormularioDeConstruccion>) {
    setFormulario((actual) => ({ ...actual, ...cambios }));
  }

  function pedirCierre() {
    if (hayCambios) setConfirmandoCierre(true);
    else onCerrar();
  }

  function guardar() {
    onGuardar(fila, { formulario, validada: validadaAlGuardar, fotos: fotosPendientes });
  }

  const comunes = { idPrefijo: "construccion-edicion", formulario, ocupada, escribir, conNota };
  const conTerreno = modo !== "oculto";

  return (
    <>
      <Dialog open onOpenChange={(abierto) => !abierto && pedirCierre()}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[calc(100vh-3.5rem)] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden p-0 sm:max-w-[70rem]"
        >
          <div className="border-divisor flex flex-none items-center gap-3 border-b-[1.5px] px-5 py-4">
            <div className="min-w-0 flex-1">
              <div className="text-ink-hint text-[0.65625rem] font-extrabold tracking-[0.07em] uppercase">
                Editar construcción
              </div>
              <div className="mt-1 flex items-center gap-2">
                <DialogTitle className="text-ink text-lg font-extrabold">{fila.tipo}</DialogTitle>
                {fila.deTerreno ? (
                  <InsigniaTerreno
                    titulo={
                      encargado
                        ? `Creada por ${encargado} en la App de terreno`
                        : "Creada en la App de terreno"
                    }
                  />
                ) : null}
              </div>
              <DialogDescription className="sr-only">
                Los datos se guardan con «Guardar construcción». Las fotos de una construcción ya
                guardada se guardan apenas las sueltas.
              </DialogDescription>
            </div>

            {vuelveASinValidar ? (
              <span className="bg-warning-soft text-warning-soft-foreground rounded-md px-2.5 py-1 text-xs font-semibold">
                Al guardar cambios vuelve a quedar sin validar
              </span>
            ) : null}

            <Button
              type="button"
              variant="outline"
              size="icon"
              aria-label="Cerrar"
              title="Cerrar"
              onClick={pedirCierre}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          </div>

          <div
            className={cn(
              "grid min-h-0 flex-1 overflow-y-auto lg:overflow-hidden",
              conTerreno ? "lg:grid-cols-[minmax(0,1fr)_22.5rem]" : "grid-cols-1",
            )}
          >
            <div className="flex flex-col gap-3.5 px-5 py-4.5 lg:overflow-y-auto">
              <RotuloDeGrupo>Identificación</RotuloDeGrupo>
              <GrupoDeIdentificacion {...comunes} />

              {fila.notas.length > 0 && modo === "sincronizado" ? (
                <div className="border-warning-dot/30 bg-warning-soft/50 flex flex-col gap-2 rounded-xl border-[1.5px] px-3 py-2.5">
                  <div className="text-warning-soft-foreground flex flex-wrap items-center gap-2">
                    <MessageSquare className="size-3.5 flex-none" aria-hidden="true" />
                    <span className="text-xs font-extrabold">
                      {fila.notas.length === 1
                        ? "Nota de terreno sobre este dato"
                        : `${fila.notas.length} notas de terreno sobre estos datos`}
                    </span>
                    <span className="text-[0.6875rem] font-semibold">
                      La nota no cambia el dato: revísalo y corrígelo si corresponde.
                    </span>
                  </div>
                  {fila.notas.map((nota) => (
                    <div
                      key={nota.campo}
                      className="bg-card border-warning-dot/30 grid items-baseline gap-2.5 rounded-lg border px-2.5 py-1.5 sm:grid-cols-[9.375rem_minmax(0,1fr)_auto]"
                    >
                      <span className="text-ink text-[0.71875rem] font-extrabold">{nota.etiqueta}</span>
                      <span className="text-ink-body min-w-0 text-xs leading-snug font-semibold text-pretty">
                        {nota.texto}
                      </span>
                      <span className="text-warning-soft-foreground font-mono text-[0.65625rem] font-semibold whitespace-nowrap">
                        Terreno · {horaDeCaptura(nota.capturado_en)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="bg-divisor-suave h-px" />
              <RotuloDeGrupo>Valorización</RotuloDeGrupo>
              <GrupoDeValorizacion {...comunes} />
              <ValorEstimado formulario={formulario} />

              <div className="bg-divisor-suave h-px" />
              {guardada ? (
                <FotosDeLaFila
                  titulo="Fotos"
                  etiquetaDelSelector="Adjuntar fotos de la construcción"
                  ocupada={ocupada}
                  editando
                  pendientes={[]}
                  guardadas={guardada.fotos ?? []}
                  urlDe={(foto) =>
                    urlDeLaFotoDeLaFila("construcciones", informeId, guardada.id, foto.id, foto.hash)
                  }
                  alt={`Foto de ${guardada.tipo_edificacion}`}
                  error={rechazoDeFoto}
                  // La fila YA existe: cada foto se va sola, sin esperar el botón
                  // de abajo — las imágenes se guardan al soltarlas.
                  onElegidas={(archivos) => archivos.forEach((a) => onSubirFoto(guardada.id, a))}
                  onRechazo={setRechazoDeFoto}
                  onQuitarPendiente={() => {}}
                  onQuitarGuardada={(fotoId) => onQuitarFoto(guardada.id, fotoId)}
                  onLeyenda={
                    onLeyendaDeFoto
                      ? (fotoId, leyenda) => onLeyendaDeFoto(guardada.id, fotoId, leyenda)
                      : undefined
                  }
                />
              ) : (
                <FotosDeLaFila
                  titulo="Fotos"
                  etiquetaDelSelector="Adjuntar fotos de la construcción"
                  ocupada={ocupada}
                  editando={false}
                  pendientes={fotosPendientes}
                  guardadas={[]}
                  urlDe={() => ""}
                  alt="Foto de la construcción"
                  error={rechazoDeFoto}
                  // Todavía no hay fila a la cual colgarlas: viajan con el alta.
                  onElegidas={(archivos) => setFotosPendientes((actuales) => [...actuales, ...archivos])}
                  onRechazo={setRechazoDeFoto}
                  onQuitarPendiente={(i) => setFotosPendientes((actuales) => actuales.filter((_, j) => j !== i))}
                  onQuitarGuardada={() => {}}
                />
              )}
            </div>

            {modo === "oculto" ? null : (
              <RegistroDeTerrenoDeLaConstruccion
                fila={fila}
                modo={modo}
                informeId={informeId}
                encargado={encargado}
                onAbrirFoto={onAbrirFoto}
              />
            )}
          </div>

          <div className="border-divisor bg-card flex flex-none flex-wrap items-center gap-2.5 border-t-[1.5px] px-5 py-3">
            <label
              className={cn(
                "flex w-fit items-center gap-2 text-xs font-bold",
                casilla ? "text-success-soft-foreground" : "text-ink-body",
              )}
            >
              <Checkbox
                checked={casilla}
                disabled={ocupada}
                onCheckedChange={(v) => {
                  setCasilla(v === true);
                  setCasillaTocada(true);
                }}
              />
              Información validada
            </label>

            <div className="ml-auto flex gap-2.5">
              <Button type="button" variant="outline" onClick={pedirCierre} disabled={ocupada}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={guardar}
                disabled={ocupada || formulario.tipo_edificacion.trim() === ""}
              >
                {ocupada ? <Spinner /> : null}
                Guardar construcción
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DialogoCambiosSinGuardar
        abierto={confirmandoCierre}
        descripcion={
          "Vas a perder los cambios sin guardar en esta construcción. Las fotos que ya se " +
          "subieron no se deshacen: siguen en el informe aunque descartes el resto."
        }
        guardando={ocupada}
        onGuardarYSalir={() => {
          setConfirmandoCierre(false);
          guardar();
        }}
        onSalirSinGuardar={() => {
          setConfirmandoCierre(false);
          onCerrar();
        }}
        onCancelar={() => setConfirmandoCierre(false)}
      />
    </>
  );
}
